
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Airline {
  id: string;
  name_ar: string;
  name_en: string;
  code: string;
  created_at: string;
  updated_at: string;
}

interface AirlineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  airline?: Airline | null;
  onClose: () => void;
}

export function AirlineDialog({ open, onOpenChange, airline, onClose }: AirlineDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    if (airline) {
      setFormData({
        name_ar: airline.name_ar,
        name_en: airline.name_en,
        code: airline.code,
      });
    } else {
      setFormData({
        name_ar: '',
        name_en: '',
        code: '',
      });
    }
  }, [airline, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (airline) {
        // تحديث شركة طيران موجودة
        const { error } = await supabase
          .from('airlines')
          .update({
            name_ar: formData.name_ar,
            name_en: formData.name_en,
            code: formData.code,
            updated_at: new Date().toISOString(),
          })
          .eq('id', airline.id);

        if (error) throw error;

        toast({
          title: "تم التحديث",
          description: "تم تحديث شركة الطيران بنجاح",
        });
      } else {
        // إضافة شركة طيران جديدة
        const { error } = await supabase
          .from('airlines')
          .insert([{
            name_ar: formData.name_ar,
            name_en: formData.name_en,
            code: formData.code,
          }]);

        if (error) throw error;

        toast({
          title: "تم الإضافة",
          description: "تم إضافة شركة الطيران بنجاح",
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving airline:', error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ شركة الطيران",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {airline ? 'تعديل شركة الطيران' : 'إضافة شركة طيران جديدة'}
          </DialogTitle>
          <DialogDescription>
            {airline 
              ? 'قم بتعديل بيانات شركة الطيران' 
              : 'أدخل بيانات شركة الطيران الجديدة'
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name_ar">الاسم بالعربية</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="اسم شركة الطيران بالعربية"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Airline Name in English"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="code">الرمز</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="مثال: SV, MS, EK"
                maxLength={3}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'جاري الحفظ...' : (airline ? 'تحديث' : 'إضافة')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
